import { To } from 'react-router-dom'

/**
 * Utilities for query values that live in the url hash. The AngularJS host routes in the hash,
 * and the standalone app keeps these values in the hash too, so both modes read and write the
 * same place.
 */

export const splitHash = (
  hash: string
): { path: string; params: URLSearchParams } => {
  const queryIndex = hash.indexOf('?')
  if (queryIndex === -1) {
    return { path: hash, params: new URLSearchParams() }
  }
  return {
    path: hash.slice(0, queryIndex),
    params: new URLSearchParams(hash.slice(queryIndex + 1)),
  }
}

export const readHashParam = (key: string): string => {
  return splitHash(window.location.hash).params.get(key) ?? ''
}

/** The route part of the url, without the leading hash. */
const currentHashPath = (): string =>
  splitHash(window.location.hash).path.replace(/^#/, '')

/** Sets each value on the params, or removes the key when the value is empty. */
const applyValues = (params: URLSearchParams, values: Record<string, string>) =>
  Object.entries(values).forEach(([key, value]) => {
    if (value) {
      params.set(key, value)
    } else {
      params.delete(key)
    }
  })

// Mirror of AngularJS encodeUriQuery: colons and a few other marks stay literal
const encodeAngularStyle = (value: string): string =>
  encodeURIComponent(value)
    .replace(/%40/gi, '@')
    .replace(/%3A/gi, ':')
    .replace(/%24/g, '$')
    .replace(/%2C/gi, ',')
    .replace(/%3B/gi, ';')

/**
 * Serializes query params the way AngularJS composes them, so a url React writes is
 * byte-identical to what $location recomposes on adoption. URLSearchParams.toString() encodes
 * colons as %3A while AngularJS keeps them literal; any byte difference makes AngularJS push
 * its own normalized copy of the url on top of the history stack, eating browser back presses.
 */
export const toCompatibleSearchString = (params: URLSearchParams): string =>
  Array.from(params.entries())
    .map(
      ([key, value]) =>
        `${encodeAngularStyle(key)}=${encodeAngularStyle(value)}`
    )
    .join('&')

/**
 * Flattens a react-router destination into the url string AngularJS needs, query string
 * included. A destination with no path of its own stays on the current page.
 */
export const toUrlString = (url: To): string => {
  if (typeof url === 'string') return url

  const pathname = url.pathname ?? currentHashPath()

  return `${pathname}${url.search ?? ''}${url.hash ?? ''}`
}

/**
 * Set or remove a query value in the url hash without triggering a router navigation, so route
 * loaders do not run again. An empty value removes the key.
 */
export const updateUrlSearchParam = (queryKey: string, queryValue: string) =>
  updateUrlSearchParams({ [queryKey]: queryValue })

/** Same as updateUrlSearchParam for several keys, so values that belong together go in one go. */
export const updateUrlSearchParams = (values: Record<string, string>) => {
  const { path, params } = splitHash(window.location.hash)
  const alreadyInUrl = Object.entries(values).every(
    ([key, value]) => value === (params.get(key) ?? '')
  )
  if (alreadyInUrl) return

  applyValues(params, values)

  // Rebuild the hash from the parsed values. set() overwrites, so no duplicate keys like tab=x&tab=x.
  // This rewrites every param in the url, not just the one that changed, so any mismatch makes
  // AngularJS replace our url with its own, and the extra history entry costs the user a back press
  const queryString = toCompatibleSearchString(params)
  const newHash = queryString ? `${path}?${queryString}` : path

  // Written straight to history rather than through useSearchParams, which navigates and
  // runs the route loader again. The data is already on the client, this only filters it.
  // The existing history state is carried over, react-router keeps its own keys in there and
  // uses them to track where it is in the history stack.
  window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + newHash
  )

  /* Tells AngularJS the url changed in React, the same event a route change emits. sameRoute
  means only the query moved, so it keeps the page it is showing (see statechangelisteners.run.js).
  The url has no leading hash, which is the shape AngularJS compares against its $location. */
  window.dispatchEvent(
    new CustomEvent('@@loaded/reactNavigation', {
      detail: {
        to: newHash.replace(/^#/, ''),
        reactInternal: true,
        sameRoute: true,
      },
    })
  )
}

/**
 * The query values already in the url with these applied on top. A router navigation needs it
 * to keep the values react-router did not write itself.
 */
export const withUrlSearchParams = (
  values: Record<string, string>
): URLSearchParams => {
  const { params } = splitHash(window.location.hash)
  applyValues(params, values)

  return params
}
